import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ContentsTopHeader from "../../../components/common/contents.top.header";
import SearchForm from "../../../components/common/search.form";
import UserInfo from "../../../components/backup/user.info";
import {
  ImageFileListContainer,
  TableContainer,
} from "../../../style/contents/image/image";
import { useAuthStore } from "../../../atoms/user.auth.store";
import { fetchDiskBackupContents } from "../../../api/admin";

export interface ImageContentItem {
  id: number;
  path: string;
  name: string;
  type: "file" | "folder" | "directory" | string;
  size: number;
  createdDate: string | number;
  s3Url: string;
}

const decodeBase64Param = (value: string | null) => {
  if (!value) return "";

  try {
    return decodeURIComponent(atob(value));
  } catch {
    return decodeURIComponent(value);
  }
};

const formatFileSize = (size: number) => {
  if (!size) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const unitIndex = Math.min(
    Math.floor(Math.log(size) / Math.log(1024)),
    units.length - 1,
  );
  const value = size / 1024 ** unitIndex;

  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const formatDate = (value: string | number) => {
  if (!value) return "-";

  const timestamp =
    typeof value === "number" || /^\d+$/.test(String(value))
      ? Number(value)
      : value;
  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) return String(value);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getTypeLabel = (type: string) => {
  if (type === "file") return "파일";
  if (type === "folder" || type === "directory") return "폴더";
  return type || "-";
};

const ImageContents = () => {
  const { role } = useAuthStore();
  const [contents, setContents] = useState<ImageContentItem[]>([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [pageSize, setPageSize] = useState("10");
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { path: rawPath = "" } = useParams<{ path: string }>();
  const queryParams = new URLSearchParams(location.search);
  const directoryPath =
    queryParams.get("directoryPath") || decodeURIComponent(rawPath);
  const targetUserId = queryParams.get("targetUserId");
  const targetDeviceId = queryParams.get("targetDeviceId");
  const diskBackupStamp = queryParams.get("diskBackupStamp");
  const userAccount = queryParams.get("userAccount");
  const pcName = decodeBase64Param(queryParams.get("pcName"));
  const adminUserId = localStorage.getItem("userId");

  const filteredContents = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return contents;

    return contents.filter((item) =>
      [
        item.name,
        item.path,
        getTypeLabel(item.type),
        item.size,
        formatFileSize(Number(item.size)),
        formatDate(item.createdDate),
      ]
        .map((value) => String(value ?? "").toLowerCase())
        .join(" ")
        .includes(keyword),
    );
  }, [contents, searchKeyword]);

  const handleFolderClick = (path: string) => {
    const nextQueryParams = new URLSearchParams(location.search);
    nextQueryParams.set("directoryPath", path);
    navigate(`/image/contents?${nextQueryParams.toString()}`);
  };

  useEffect(() => {
    const loadContents = async () => {
      if (!adminUserId || !targetUserId || !targetDeviceId || !diskBackupStamp) {
        return;
      }

      setIsLoading(true);
      try {
        const response = await fetchDiskBackupContents(adminUserId, {
          targetUserId: Number(targetUserId),
          targetDeviceId,
          diskBackupStamp,
          directoryPath,
        });

        setContents(response.contents || []);
      } catch (err) {
        console.error("드라이브 이미지 콘텐츠 조회 실패:", err);
      } finally {
        setIsLoading(false);
      }
    };

    if (role === "admin") loadContents();
  }, [adminUserId, directoryPath, diskBackupStamp, role, targetDeviceId, targetUserId]);

  return (
    <ImageFileListContainer>
      <ContentsTopHeader
        title="드라이브 이미지 백업 목록  > 콘텐츠 목록"
        backArrowPath="-1"
      />
      <div className="contents-top">
        <div className="device-name">
          <p>
            {pcName}({targetDeviceId})
          </p>
        </div>
        <div className="device-info">
          <p>현재 경로 : {directoryPath}</p>
          {role === "admin" && <UserInfo userAccount={userAccount || ""} />}
        </div>
      </div>
      <div className="file-list-container">
        <div className="top">
          <div className="path-name">{directoryPath}</div>
          <div className="right">
            <SearchForm
              onSearch={setSearchKeyword}
              onPageSizeChange={setPageSize}
              pageSize={pageSize}
            />
          </div>
        </div>
        <TableContainer>
          <div className="table-head-wrap">
            <div className="left">
              <span className="table-total-row">
                전체 {filteredContents.length}
              </span>
            </div>
          </div>
          {isLoading ? (
            <div className="loading">데이터를 불러오는 중입니다...</div>
          ) : (
            <div className="fixed-height">
              <table>
                <thead>
                  <tr>
                    <th>종류</th>
                    <th>이름</th>
                    <th>크기</th>
                    <th>생성일</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContents.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="no-data-row">
                        검색 결과가 없습니다.
                      </td>
                    </tr>
                  ) : (
                    filteredContents
                      .slice(0, Number(pageSize))
                      .map((item) => {
                        const isFolder =
                          item.type === "folder" || item.type === "directory";

                        return (
                          <tr key={item.id}>
                            <td>{getTypeLabel(item.type)}</td>
                            <td>
                              {isFolder ? (
                                <span
                                  className="clickable-folder"
                                  onClick={() => handleFolderClick(item.path)}
                                >
                                  {item.name}
                                </span>
                              ) : (
                                item.name
                              )}
                            </td>
                            <td>{formatFileSize(Number(item.size))}</td>
                            <td>{formatDate(item.createdDate)}</td>
                          </tr>
                        );
                      })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </TableContainer>
      </div>
    </ImageFileListContainer>
  );
};

export default ImageContents;
